import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { api } from '@/lib/db';
import { useAuth } from '@/contexts/AuthContext';
import { Card, Badge } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const fetchStats = async () => {
      const data = await api.getAdminDashboardStats(user.business_id);
      setStats(data);
    };
    fetchStats();
  }, [user.business_id]);

  // Dark mode figyelés a chart frissítéséhez
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (!stats) return <div className="flex justify-center p-20 animate-pulse text-gray-500 dark:text-gray-400">Betöltés...</div>;

  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: isDark ? '#1e293b' : '#fff',
      borderColor: isDark ? '#334155' : '#e5e7eb',
      textStyle: { color: isDark ? '#f1f5f9' : '#111827' },
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['1★', '2★', '3★', '4★', '5★'],
      axisLine: { show: false },
      axisLabel: { color: isDark ? '#94a3b8' : '#6b7280' },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed', color: isDark ? '#334155' : '#e5e7eb' } },
      axisLabel: { color: isDark ? '#94a3b8' : '#6b7280' },
    },
    series: [{
      name: 'Kattintások',
      type: 'bar',
      barWidth: '50%',
      itemStyle: {
        borderRadius: [8, 8, 0, 0],
        color: (params) => params.dataIndex >= 3 ? '#10b981' : '#f59e0b'
      },
      data: stats.ratingBreakdown.map(r => r.count)
    }]
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black text-gray-900 dark:text-white">Vezérlőpult</h1>
        <p className="text-gray-500 dark:text-gray-400">Itt látod, hogyan teljesítenek az értékeléseid – és mennyit mentettél meg közülük</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 border-l-4 border-blue-500">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">Összes aktivitás</p>
          <div className="flex items-end justify-between mt-2">
            <h3 className="text-3xl font-black text-gray-900 dark:text-white">{stats.ratingBreakdown.reduce((acc, curr) => acc + curr.count, 0)}</h3>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400"><SafeIcon name="Activity" /></div>
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-green-500">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">Google átirányítások</p>
          <div className="flex items-end justify-between mt-2">
            <h3 className="text-3xl font-black text-green-600 dark:text-green-400">{stats.redirects}</h3>
            <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg text-green-600 dark:text-green-400"><SafeIcon name="ExternalLink" /></div>
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-amber-500">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">Beérkezett visszajelzések</p>
          <div className="flex items-end justify-between mt-2">
            <h3 className="text-3xl font-black text-amber-600 dark:text-amber-400">{stats.captured}</h3>
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg text-amber-600 dark:text-amber-400"><SafeIcon name="ShieldAlert" /></div>
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-purple-500">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">Legaktívabb helyszín</p>
          <div className="mt-2">
            <h3 className="text-xl font-black text-gray-900 dark:text-white truncate">{stats.topLocation?.name || '-'}</h3>
            <p className="text-xs text-purple-600 dark:text-purple-400 font-bold">{stats.topLocation?.redirects || 0} Google átirányítás</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2 p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <SafeIcon name="BarChart" className="text-blue-600" /> Értékelések Eloszlása
          </h3>
          <ReactECharts option={chartOptions} style={{ height: '300px' }} />
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <SafeIcon name="Clock" className="text-blue-600" /> Legutóbbi visszajelzések
          </h3>
          <div className="space-y-4">
            {stats.recentReviews.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-10 italic">Még nincs beérkezett visszajelzés</p>
            ) : (
              stats.recentReviews.map(review => (
                <div key={review.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700 transition-colors">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{review.name}</span>
                    <Badge variant={review.star_rating === 3 ? 'yellow' : 'red'}>{review.star_rating}★</Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 italic mb-2">"{review.message}"</p>
                  <div className="flex justify-between items-center text-[10px] text-gray-400 dark:text-gray-500">
                    <span className="font-medium">{review.location_name}</span>
                    <span>{format(new Date(review.created_at), 'MMM d.', { locale: hu })}</span>
                  </div>
                </div>
              ))
            )}
            <a href="/#/admin/reviews" className="block text-center text-sm font-bold text-blue-600 dark:text-blue-400 hover:underline pt-2">Összes megtekintése</a>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
